import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Label } from "@bookdental/ui";
import { BRAZILIAN_STATES } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { fetchAddressFromCep, formatCep } from "@/lib/viacep";
import { enderecoCompleto, enderecoParaSalvar } from "@/components/profile/AddressFormFields";
import { BlocoDeDados, BotaoSalvar } from "./BlocoDeDados";

type Endereco = {
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  neighborhood: string;
  city: string;
  state: string;
};

/** Endereço principal da organização, com o mesmo ViaCEP do formulário de criar organização. */
export function CartaoDeEndereco({ canManage }: { canManage: boolean }) {
  const utils = trpc.useUtils();
  const perfil = trpc.organization.getProfiles.useQuery().data;
  const [endereco, setEndereco] = useState<Endereco | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [cepNaoAchado, setCepNaoAchado] = useState(false);

  const end: Endereco = endereco ?? {
    zipCode: perfil?.mainAddress?.zipCode ?? "",
    address: perfil?.mainAddress?.address ?? "",
    addressNumber: perfil?.mainAddress?.addressNumber ?? "",
    addressComplement: perfil?.mainAddress?.addressComplement ?? "",
    neighborhood: perfil?.mainAddress?.neighborhood ?? "",
    city: perfil?.mainAddress?.city ?? "",
    state: perfil?.mainAddress?.state ?? "",
  };

  const salvar = trpc.organization.updateMainAddress.useMutation({
    onSuccess: () => {
      toast.success("Endereço atualizado.");
      setEndereco(null);
      utils.organization.getProfiles.invalidate();
      utils.me.current.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar o endereço."),
  });

  const buscarCep = async () => {
    setCepNaoAchado(false);
    const digitos = end.zipCode.replace(/\D/g, "");
    if (digitos.length !== 8) return;
    setBuscando(true);
    const achado = await fetchAddressFromCep(digitos);
    setBuscando(false);
    if (!achado) {
      setCepNaoAchado(true);
      return;
    }
    setEndereco({
      ...end,
      zipCode: achado.zipCode,
      address: achado.address || end.address,
      neighborhood: achado.neighborhood || end.neighborhood,
      city: achado.city || end.city,
      state: achado.state || end.state,
    });
  };

  const mudar = (campo: keyof Endereco, valor: string) => setEndereco({ ...end, [campo]: valor });
  const campo = (id: string, rotulo: string, chave: keyof Endereco, span: string) => (
    <div className={`space-y-1.5 ${span}`}>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input id={id} value={end[chave]} disabled={!canManage} onChange={(e) => mudar(chave, e.target.value)} />
    </div>
  );

  return (
    <BlocoDeDados titulo="Endereço" descricao="Endereço principal da organização. Busque pelo CEP pra preencher automaticamente.">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
        <div className="space-y-1.5 sm:col-span-5">
          <Label htmlFor="org-cep">CEP</Label>
          <div className="flex gap-2">
            <Input
              id="org-cep"
              placeholder="01310-100"
              inputMode="numeric"
              value={formatCep(end.zipCode)}
              disabled={!canManage}
              onChange={(e) => {
                setCepNaoAchado(false);
                mudar("zipCode", e.target.value.replace(/\D/g, ""));
              }}
              onBlur={buscarCep}
            />
            <Button
              type="button"
              variant="outline"
              onClick={buscarCep}
              disabled={!canManage || buscando || end.zipCode.replace(/\D/g, "").length !== 8}
              aria-label="Buscar CEP"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          {cepNaoAchado && <p className="text-xs text-amber-700">CEP não encontrado. Preencha manualmente.</p>}
        </div>
        {campo("org-endereco", "Logradouro", "address", "sm:col-span-7")}
        {campo("org-numero", "Número", "addressNumber", "sm:col-span-3")}
        {campo("org-complemento", "Complemento", "addressComplement", "sm:col-span-4")}
        {campo("org-bairro", "Bairro", "neighborhood", "sm:col-span-5")}
        {campo("org-cidade", "Cidade", "city", "sm:col-span-9")}
        <div className="space-y-1.5 sm:col-span-3">
          <Label htmlFor="org-uf">Estado (UF)</Label>
          <select
            id="org-uf"
            value={end.state}
            disabled={!canManage}
            onChange={(e) => mudar("state", e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="">—</option>
            {BRAZILIAN_STATES.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>
      </div>
      {canManage && (
        <BotaoSalvar
          onClick={() => salvar.mutate(enderecoParaSalvar(end))}
          disabled={salvar.isPending || endereco === null || !enderecoCompleto(end)}
          salvando={salvar.isPending}
          title={enderecoCompleto(end) ? undefined : "Preencha CEP, logradouro, número, bairro, cidade e UF"}
        />
      )}
    </BlocoDeDados>
  );
}
